const SBSA_ENTITY_NAME = 'SBSA';
const SBG_GROUP_NAME = 'SBG';

const EXCLUDED_ENTITY_NAMES = [
    'TCM SA',
    'CIB SA',
    'NFR SA',
    'BCB SA',
    'PBB SA',
    'Central Credit SA',
];

const normalizeName = (value) => (value ?? '').trim().toLowerCase();
const normalizedSbsaName = normalizeName(SBSA_ENTITY_NAME);
const normalizedExcludedNames = new Set(EXCLUDED_ENTITY_NAMES.map(normalizeName));

const isSbsaEntity = (name) => normalizeName(name) === normalizedSbsaName;
const isExcludedEntity = (name) => normalizedExcludedNames.has(normalizeName(name));

// Average all values — Not Assessed (0) is included in the denominator.
const averageAllRatings = (values) => {
    const all = (values ?? []).map((v) => Number(v)).filter((v) => Number.isFinite(v));
    if (!all.length) return 0;
    return parseFloat((all.reduce((sum, value) => sum + value, 0) / all.length).toFixed(2));
};

// Read the pre-computed flat average from the backend — identical to the SARB chip formula.
const getEntityRating = (report, entityId) => {
    const ratings = report?.entityOverallRatings;
    if (!ratings) return 0;
    const value = ratings[entityId] ?? ratings[String(entityId)];
    return value != null ? parseFloat(Number(value).toFixed(2)) : 0;
};

const buildRawRows = (allEntities, septemberReport, decemberReport) =>
    (allEntities ?? [])
        .filter((entity) => entity?.name)
        .map((entity) => ({
            id: entity.id,
            name: entity.name,
            septemberRating: getEntityRating(septemberReport, entity.id),
            decemberRating: getEntityRating(decemberReport, entity.id),
        }));

const buildSbsaAggregateRow = (rawRows) => {
    const sbsaAndChildren = rawRows.filter(
        (row) => isSbsaEntity(row.name) || isExcludedEntity(row.name)
    );
    if (!sbsaAndChildren.length) return null;

    return {
        name: SBSA_ENTITY_NAME,
        septemberRating: averageAllRatings(sbsaAndChildren.map((row) => row.septemberRating)),
        decemberRating: averageAllRatings(sbsaAndChildren.map((row) => row.decemberRating)),
    };
};

const buildRowsWithoutExcludedChildren = (rawRows) => {
    const sbsaAggregate = buildSbsaAggregateRow(rawRows);
    const rows = [];

    rawRows.forEach((row) => {
        if (isExcludedEntity(row.name) || isSbsaEntity(row.name)) return;

        rows.push({
            name: row.name,
            septemberRating: row.septemberRating,
            decemberRating: row.decemberRating,
        });
    });

    // SBSA aggregate always appears at the end, before SBG Group
    if (sbsaAggregate) rows.push(sbsaAggregate);

    return rows;
};

export const buildConsolidatedRows = ({
    allEntities = [],
    septemberReport = null,
    decemberReport = null,
} = {}) => {
    const rawRows = buildRawRows(allEntities, septemberReport, decemberReport);
    const rowsWithoutChildren = buildRowsWithoutExcludedChildren(rawRows);

    if (!rowsWithoutChildren.length) return [];

    const sbgGroupRow = {
        name: SBG_GROUP_NAME,
        septemberRating: averageAllRatings(rowsWithoutChildren.map((row) => row.septemberRating)),
        decemberRating: averageAllRatings(rowsWithoutChildren.map((row) => row.decemberRating)),
    };

    // SBG Group is always last — after SBSA which is already at the end of rowsWithoutChildren
    return [...rowsWithoutChildren, sbgGroupRow];
};
